import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Phone, Calendar, User, LogOut, Clock, MapPin, ChevronRight, Loader2, Plus, X, ArrowLeft, Stethoscope, Bell, Trash2, Download, FileText, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { PwaInstallBanner } from '../components/PwaInstallBanner';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== AUTH CONTEXT ====================
const PatientAuthContext = createContext(null);

function usePatientAuth() {
  return useContext(PatientAuthContext);
}

function PatientAuthProvider({ children }) {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clinicInfo, setClinicInfo] = useState({ name: 'Centro Medico', phone: '' });

  useEffect(() => {
    const token = localStorage.getItem('patient_token');
    if (token) {
      axios.get(`${API}/api/portal/patient/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setPatient(res.data))
        .catch(() => { localStorage.removeItem('patient_token'); setPatient(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // Load clinic info
    axios.get(`${API}/api/portal/clinic-info`).then(res => setClinicInfo(res.data)).catch(() => {});
  }, []);

  const login = async (phone, dob) => {
    const res = await axios.post(`${API}/api/portal/patient/login`, { phone, date_of_birth: dob });
    localStorage.setItem('patient_token', res.data.access_token);
    setPatient(res.data.patient);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('patient_token');
    setPatient(null);
  };

  return (
    <PatientAuthContext.Provider value={{ patient, loading, login, logout, isAuthenticated: !!patient, clinicInfo }}>
      {children}
    </PatientAuthContext.Provider>
  );
}

function patientApi() {
  const token = localStorage.getItem('patient_token');
  return axios.create({
    baseURL: `${API}/api/portal/patient`,
    headers: { Authorization: `Bearer ${token}` }
  });
}

// ==================== COMPONENTS ====================
function MobileShell({ children, title, showBack, onBack }) {
  const { patient, logout } = usePatientAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col max-w-lg mx-auto" data-testid="patient-shell">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          {showBack && (
            <button onClick={onBack || (() => navigate(-1))} className="p-1">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <h1 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Manrope' }}>{title}</h1>
        </div>
        {patient && (
          <button onClick={logout} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="patient-logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        )}
      </header>
      {/* Content */}
      <main className="flex-1 p-4">
        <PwaInstallBanner />
        {children}
      </main>
      {/* Bottom Nav */}
      {patient && <BottomNav />}
    </div>
  );
}

function BottomNav() {
  const location = useLocation();
  const nav = [
    { to: '/paziente', icon: Calendar, label: 'Appuntamenti' },
    { to: '/paziente/ricette', icon: FileText, label: 'Ricette' },
    { to: '/paziente/prenota', icon: Plus, label: 'Prenota' },
    { to: '/paziente/profilo', icon: User, label: 'Profilo' }
  ];
  return (
    <nav className="bg-white border-t border-slate-200 flex sticky bottom-0">
      {nav.map(item => {
        const active = location.pathname === item.to;
        return (
          <Link key={item.to} to={item.to} className={`flex-1 flex flex-col items-center py-2.5 text-xs transition-colors ${active ? 'text-sky-600' : 'text-slate-400'}`} data-testid={`nav-${item.label.toLowerCase()}`}>
            <item.icon className="w-5 h-5 mb-0.5" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function ClinicCallButton() {
  const { clinicInfo } = usePatientAuth();
  const phoneNumber = clinicInfo?.phone || clinicInfo?.phone_number;
  if (!phoneNumber) return null;
  return (
    <a href={`tel:${phoneNumber}`} className="block bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors" data-testid="click-to-call">
      <div className="flex items-center gap-3">
        <Phone className="w-5 h-5 text-emerald-600" />
        <div>
          <p className="font-medium text-emerald-800 text-sm">Chiama il Centro Medico</p>
          <p className="text-emerald-600 text-xs">{phoneNumber}</p>
        </div>
      </div>
    </a>
  );
}

// ==================== LOGIN ====================
function PatientLoginPage() {
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated, clinicInfo } = usePatientAuth();
  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/paziente" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(phone, dob);
      navigate('/paziente');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di accesso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm" data-testid="patient-login-page">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Phone className="w-8 h-8 text-sky-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Area Paziente</h1>
          <p className="text-slate-500 text-sm mt-1">{clinicInfo.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3" data-testid="login-error">{error}</div>}
          
          <div>
            <Label className="text-slate-700">Numero di Telefono</Label>
            <Input
              type="tel"
              placeholder="+39 333 1234567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              required
              data-testid="patient-phone-input"
            />
          </div>
          
          <div>
            <Label className="text-slate-700">Data di Nascita</Label>
            <Input
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              required
              data-testid="patient-dob-input"
            />
          </div>

          <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600 text-white" disabled={loading} data-testid="patient-login-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accedi'}
          </Button>
        </form>

        <div className="text-center mt-6">
          {(clinicInfo.phone || clinicInfo.phone_number) && (
            <a href={`tel:${clinicInfo.phone || clinicInfo.phone_number}`} className="inline-flex items-center gap-2 text-sky-600 text-sm hover:text-sky-700">
              <Phone className="w-4 h-4" />
              Chiama il centro: {clinicInfo.phone || clinicInfo.phone_number}
            </a>
          )}
        </div>

        <div className="text-center mt-3">
          <Link to="/login" className="text-xs text-slate-400 hover:text-slate-600">Accesso staff</Link>
        </div>
      </div>
    </div>
  );
}

// ==================== APPOINTMENTS LIST ====================
function PatientAppointments() {
  const { patient } = usePatientAuth();
  const [appointments, setAppointments] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [apptRes, waitRes] = await Promise.all([
        patientApi().get('/appointments'),
        patientApi().get('/waitlist')
      ]);
      setAppointments(apptRes.data);
      setWaitlistEntries(waitRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (id) => {
    try {
      await patientApi().post(`/cancel/${id}`);
      toast.success('Appuntamento cancellato');
      loadData();
    } catch (err) {
      toast.error('Errore nella cancellazione');
    }
  };

  const leaveWaitlist = async (id) => {
    try {
      await patientApi().delete(`/waitlist/${id}`);
      toast.success('Rimosso dalla lista d\'attesa');
      loadData();
    } catch (err) {
      toast.error('Errore nella rimozione');
    }
  };

  const upcoming = appointments.filter(a => a.date >= new Date().toISOString().split('T')[0] && a.status !== 'cancelled');
  const past = appointments.filter(a => a.date < new Date().toISOString().split('T')[0] || a.status === 'cancelled');

  return (
    <MobileShell title={`Ciao, ${patient?.name?.split(' ')[0] || 'Paziente'}`}>
      <div className="space-y-6" data-testid="patient-appointments">
        {/* Quick Actions */}
        <Link to="/paziente/prenota" className="block bg-sky-500 text-white rounded-xl p-4 hover:bg-sky-600 transition-colors" data-testid="quick-book-btn">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plus className="w-5 h-5" />
              <span className="font-medium">Prenota una visita</span>
            </div>
            <ChevronRight className="w-5 h-5" />
          </div>
        </Link>

        {/* Click to call */}
        <ClinicCallButton />

        {/* Waitlist entries */}
        {waitlistEntries.length > 0 && (
          <div data-testid="patient-waitlist-section">
            <h2 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-amber-500" />
              In Lista d{"'"}Attesa
            </h2>
            <div className="space-y-2">
              {waitlistEntries.map(entry => (
                <div key={entry.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid={`waitlist-entry-${entry.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-amber-800 text-sm">{entry.service}</p>
                      <p className="text-amber-600 text-xs mt-0.5">{entry.department_name}</p>
                      <p className="text-amber-500 text-xs mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {entry.status === 'notified' ? 'Posto disponibile! Controlla la tua email.' : 'Ti avviseremo quando si libera un posto'}
                      </p>
                    </div>
                    <button
                      onClick={() => leaveWaitlist(entry.id)}
                      className="p-2 rounded-lg text-amber-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      data-testid={`leave-waitlist-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming */}
        <div>
          <h2 className="font-semibold text-slate-700 text-sm mb-3">Prossimi Appuntamenti</h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
          ) : upcoming.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
              <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Nessun appuntamento in programma</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map(appt => (
                <AppointmentCard key={appt.id} appt={appt} onCancel={() => cancelAppointment(appt.id)} />
              ))}
            </div>
          )}
        </div>

        {/* Past */}
        {past.length > 0 && (
          <div>
            <h2 className="font-semibold text-slate-700 text-sm mb-3">Storico</h2>
            <div className="space-y-3">
              {past.slice(0, 5).map(appt => (
                <AppointmentCard key={appt.id} appt={appt} isPast />
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}

function AppointmentCard({ appt, onCancel, isPast }) {
  const dateObj = new Date(appt.date + 'T00:00:00');
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  return (
    <div className={`bg-white rounded-xl border p-4 ${isPast || appt.status === 'cancelled' ? 'border-slate-100 opacity-60' : 'border-slate-200'}`} data-testid="appointment-card">
      <div className="flex gap-3">
        <div className="bg-sky-50 rounded-lg px-3 py-2 text-center min-w-[56px]">
          <p className="text-xs text-sky-600 font-medium">{dayNames[dateObj.getDay()]}</p>
          <p className="text-xl font-bold text-sky-700">{dateObj.getDate()}</p>
          <p className="text-xs text-sky-600">{monthNames[dateObj.getMonth()]}</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 text-sm">{appt.service}</p>
          <div className="flex items-center gap-1.5 mt-1 text-slate-500 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>{appt.time}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-slate-500 text-xs">
            <Stethoscope className="w-3.5 h-3.5" />
            <span>{appt.doctor_name || 'Da assegnare'}</span>
          </div>
          {appt.status === 'cancelled' && (
            <span className="inline-block mt-1 text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">Cancellato</span>
          )}
        </div>
        {!isPast && appt.status !== 'cancelled' && onCancel && (
          <button onClick={onCancel} className="text-slate-300 hover:text-red-500 transition-colors self-start" data-testid="cancel-appt-btn">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ==================== BOOKING ====================
function PatientBooking() {
  const [step, setStep] = useState(1); // 1=service, 2=slot, 3=confirm
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState({ service: null, departmentId: null, slot: null, homeService: false });
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [homeAddress, setHomeAddress] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    patientApi().get('/services').then(res => { setServices(res.data); setLoading(false); });
  }, []);

  const selectService = async (svc) => {
    setSelected(s => ({ ...s, service: svc.service, departmentId: svc.department_id, homeService: svc.home_service_available || false }));
    setLoading(true);
    setWaitlistJoined(false);
    try {
      const res = await patientApi().get(`/slots?service=${svc.service}&department_id=${svc.department_id}`);
      setSlots(res.data);
    } catch (err) { toast.error('Errore nel caricamento slot'); }
    setLoading(false);
    setStep(2);
  };

  const selectSlot = (slot) => {
    setSelected(s => ({ ...s, slot }));
    setStep(3);
  };

  const confirmBooking = async () => {
    setBooking(true);
    try {
      const bookingData = {
        service: selected.service,
        department_id: selected.departmentId,
        doctor_id: selected.slot.doctor_id,
        date: selected.slot.date,
        time: selected.slot.time,
      };
      if (homeAddress.trim()) {
        bookingData.service_type = 'home_visit';
        bookingData.home_address = homeAddress.trim();
      }
      await patientApi().post('/book', bookingData);
      toast.success('Prenotazione confermata!');
      navigate('/paziente');
    } catch (err) {
      toast.error('Errore nella prenotazione');
    } finally {
      setBooking(false);
    }
  };

  const joinWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await patientApi().post('/waitlist', {
        service: selected.service,
        department_id: selected.departmentId,
        notes: ''
      });
      toast.success('Iscritto alla lista d\'attesa! Ti avviseremo via email.');
      setWaitlistJoined(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore';
      toast.error(msg);
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // Group slots by date
  const slotsByDate = {};
  slots.forEach(s => { (slotsByDate[s.date] = slotsByDate[s.date] || []).push(s); });

  return (
    <MobileShell title="Prenota Visita" showBack onBack={() => step > 1 ? setStep(step - 1) : navigate('/paziente')}>
      <div data-testid="patient-booking">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-sky-500' : 'bg-slate-200'}`} />
          ))}
        </div>

        {step === 1 && (
          <div>
            <h2 className="font-semibold text-slate-800 mb-4">Scegli il servizio</h2>
            {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div> : (
              <div className="space-y-2">
                {services.map((svc, i) => (
                  <button key={i} onClick={() => selectService(svc)} className="w-full bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-sky-300 transition-colors" data-testid={`service-${svc.service}`}>
                    <p className="font-medium text-slate-800">{svc.service}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{svc.department_name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="font-semibold text-slate-800 mb-1">Scegli data e ora</h2>
            <p className="text-sm text-slate-500 mb-4">{selected.service}</p>
            {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div> : 
              Object.keys(slotsByDate).length === 0 ? (
                /* No slots available - show waitlist widget */
                <div className="space-y-4" data-testid="no-slots-waitlist">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
                    <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <h3 className="font-semibold text-amber-800 text-base mb-1">Nessuno slot disponibile</h3>
                    <p className="text-amber-600 text-sm">
                      Al momento non ci sono posti liberi per <strong>{selected.service}</strong> nei prossimi giorni.
                    </p>
                  </div>

                  {waitlistJoined ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="waitlist-joined-msg">
                      <Bell className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                      <h3 className="font-semibold text-emerald-800 text-sm mb-1">{"Sei in lista d'attesa!"}</h3>
                      <p className="text-emerald-600 text-xs">
                        {"Ti invieremo un'email non appena si libera un posto per "}{selected.service}.
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={joinWaitlist}
                      disabled={joiningWaitlist}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl p-4 font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="join-waitlist-btn"
                    >
                      {joiningWaitlist ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4" />
                      )}
                      {joiningWaitlist ? 'Iscrizione in corso...' : 'Avvisami quando si libera un posto'}
                    </button>
                  )}

                  <button
                    onClick={() => setStep(1)}
                    className="w-full text-slate-500 text-sm py-2 hover:text-slate-700"
                  >
                    Scegli un altro servizio
                  </button>
                </div>
              ) : (
              <div className="space-y-4">
                {Object.entries(slotsByDate).map(([date, dateSlots]) => (
                  <div key={date}>
                    <p className="text-sm font-medium text-slate-600 mb-2 capitalize">{formatDate(date)}</p>
                    <div className="flex flex-wrap gap-2">
                      {dateSlots.map((slot, i) => (
                        <button key={i} onClick={() => selectSlot(slot)} className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm font-medium hover:border-sky-400 hover:text-sky-600 transition-colors" data-testid={`slot-${slot.date}-${slot.time}`}>
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && selected.slot && (
          <div>
            <h2 className="font-semibold text-slate-800 mb-4">Conferma prenotazione</h2>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="booking-summary">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{selected.service}</p>
                  <p className="text-xs text-slate-500">Durata: 30 minuti</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800 capitalize">{formatDate(selected.slot.date)}</p>
                  <p className="text-xs text-slate-500">Ore {selected.slot.time}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{selected.slot.doctor_name}</p>
                  <p className="text-xs text-slate-500">Medico</p>
                </div>
              </div>
            </div>
            <Button onClick={confirmBooking} disabled={booking} className="w-full mt-6 bg-sky-500 hover:bg-sky-600 text-white h-12 text-base" data-testid="confirm-booking-btn">
              {booking ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Conferma Prenotazione'}
            </Button>
          </div>
        )}
      </div>
    </MobileShell>
  );
}

// ==================== PROFILE ====================
function PatientProfile() {
  const { patient } = usePatientAuth();
  return (
    <MobileShell title="Il Mio Profilo">
      <div className="space-y-4" data-testid="patient-profile">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center">
              <User className="w-7 h-7 text-sky-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">{patient?.name}</p>
              <p className="text-sm text-slate-500">{patient?.phone}</p>
            </div>
          </div>
          {patient?.email && (
            <div className="border-t border-slate-100 pt-3 mt-3">
              <p className="text-xs text-slate-400">Email</p>
              <p className="text-sm text-slate-700">{patient.email}</p>
            </div>
          )}
        </div>

        <ClinicCallButton />

        <div className="text-center pt-4">
          <p className="text-xs text-slate-400">VoiceBook by TelcaVoIP AI</p>
        </div>
      </div>
    </MobileShell>
  );
}

// ==================== PATIENT PRESCRIPTIONS ====================
function PatientPrescriptions() {
  const { patient, patientApi } = usePatientAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [medications, setMedications] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPrescriptions = async () => {
    try {
      const res = await patientApi().get('/prescriptions');
      setPrescriptions(res.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const fetchDoctors = async () => {
    try {
      const res = await patientApi().get('/services');
      const allDoctors = [];
      const seen = new Set();
      (res.data || []).forEach(d => {
        (d.doctors || []).forEach(doc => {
          if (!seen.has(doc.id)) { seen.add(doc.id); allDoctors.push(doc); }
        });
      });
      setDoctors(allDoctors);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchPrescriptions(); fetchDoctors(); }, []);

  const handleRequest = async () => {
    if (!medications.trim() && !notes.trim()) return toast.error('Indica almeno un farmaco o una nota');
    setSubmitting(true);
    try {
      const medsArray = medications.split(',').map(m => m.trim()).filter(Boolean);
      await patientApi().post('/prescriptions/request', {
        medications: medsArray,
        notes,
        doctor_id: selectedDoctor,
      });
      toast.success('Richiesta inviata al medico!');
      setShowRequest(false);
      setMedications('');
      setNotes('');
      setSelectedDoctor('');
      fetchPrescriptions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio richiesta'); }
    setSubmitting(false);
  };

  const handleDownload = async (id, fileName) => {
    try {
      const res = await patientApi().get(`/prescriptions/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'ricetta';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  return (
    <MobileShell title="Le mie Ricette">
      <div className="space-y-4" data-testid="patient-prescriptions">
        <Button onClick={() => setShowRequest(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="request-prescription-btn">
          <Send className="w-4 h-4 mr-1.5" /> Richiedi Ricetta
        </Button>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : prescriptions.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Nessuna ricetta</p>
          </div>
        ) : (
          <div className="space-y-2">
            {prescriptions.map(p => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`presc-${p.id}`}>
                <div className="flex items-start justify-between mb-1">
                  <div>
                    {p.doctor_name && <p className="text-xs text-slate-500">Dr. {p.doctor_name}</p>}
                    <p className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString('it-IT') : ''}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    p.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {p.status === 'pending' ? 'In attesa' : p.status === 'approved' ? 'Pronta' : 'Rifiutata'}
                  </span>
                </div>
                {p.medications?.length > 0 && (
                  <p className="text-sm text-slate-700 mb-1">
                    {p.medications.map((m, i) => <span key={i}>{typeof m === 'string' ? m : m.name}{i < p.medications.length - 1 ? ', ' : ''}</span>)}
                  </p>
                )}
                {p.notes && <p className="text-xs text-slate-500 mb-1">{p.notes}</p>}
                {p.status === 'denied' && p.denial_reason && <p className="text-xs text-red-500">Motivo: {p.denial_reason}</p>}
                {p.has_file && p.status === 'approved' && (
                  <button onClick={() => handleDownload(p.id, p.file_name)}
                    className="mt-2 flex items-center gap-1.5 text-emerald-600 text-sm font-medium hover:text-emerald-700" data-testid={`download-${p.id}`}>
                    <Download className="w-4 h-4" /> Scarica {p.file_name}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Request modal */}
        {showRequest && (
          <div className="fixed inset-0 bg-black/40 flex items-end z-50">
            <div className="bg-white rounded-t-2xl w-full max-w-lg mx-auto p-5 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Richiedi Ricetta</h3>
                <button onClick={() => setShowRequest(false)} className="text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              {doctors.length > 0 && (
                <div>
                  <Label>Medico</Label>
                  <select value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)}
                    className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="select-doctor">
                    <option value="">Qualsiasi medico</option>
                    {doctors.map(d => <option key={d.id} value={d.id}>Dr. {d.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label>Farmaci richiesti</Label>
                <Input value={medications} onChange={(e) => setMedications(e.target.value)}
                  placeholder="Es. Cardioaspirina, Ramipril" className="mt-1.5" data-testid="medications-input" />
                <p className="text-xs text-slate-400 mt-1">Separa i farmaci con una virgola</p>
              </div>
              <div>
                <Label>Note aggiuntive</Label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Es. Rinnovo terapia cronica" rows={3}
                  className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" data-testid="notes-input" />
              </div>
              <Button onClick={handleRequest} disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-request-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                {submitting ? 'Invio...' : 'Invia Richiesta'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}

// ==================== MAIN PATIENT APP ====================
export default function PatientPortal() {
  return (
    <PatientAuthProvider>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="login" element={<PatientLoginPage />} />
        <Route path="" element={
          <RequirePatientAuth><PatientAppointments /></RequirePatientAuth>
        } />
        <Route path="ricette" element={
          <RequirePatientAuth><PatientPrescriptions /></RequirePatientAuth>
        } />
        <Route path="prenota" element={
          <RequirePatientAuth><PatientBooking /></RequirePatientAuth>
        } />
        <Route path="profilo" element={
          <RequirePatientAuth><PatientProfile /></RequirePatientAuth>
        } />
        <Route path="*" element={<Navigate to="/paziente" replace />} />
      </Routes>
    </PatientAuthProvider>
  );
}

function RequirePatientAuth({ children }) {
  const { isAuthenticated, loading } = usePatientAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!isAuthenticated) return <Navigate to="/paziente/login" replace />;
  return children;
}
